import React, { ReactNode, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

interface IFadeTransitionProps {
	in?: boolean;
	timeout: number;
	children: ReactNode;
}

const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
	if (typeof ref === 'function') {
		ref(value);
	} else if (ref && typeof ref === 'object') {
		(ref as React.MutableRefObject<T | null>).current = value;
	}
};

const FadeTransition: React.FC<IFadeTransitionProps> = ({ in: inProp, timeout, children }) => {
	const nodeRef = useRef<HTMLDivElement>(null);
	const child = React.Children.only(children) as React.ReactElement & { ref?: React.Ref<HTMLDivElement> };
	const isNativeElement = typeof child.type === 'string';

	const setNodeRef = (node: HTMLDivElement | null) => {
		(nodeRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
		if (isNativeElement) {
			assignRef(child.ref, node);
		}
	};

	return (
		<CSSTransition nodeRef={nodeRef} in={inProp} timeout={timeout} unmountOnExit classNames="fade">
			{isNativeElement ? (
				React.cloneElement(child, { ref: setNodeRef })
			) : (
				<div ref={setNodeRef}>{child}</div>
			)}
		</CSSTransition>
	);
};

export default React.memo(FadeTransition);
